// App-side delegate for `@heidi/session/data` (ARCFE-1004). The cache
// contract and the error CLASSIFICATION moved; this file keeps everything
// user-visible — five toasts, both quota surfaces, the delay-upgrade atoms
// and every analytics dispatch — plus `generateDocument` itself, which is
// injected as a port (E3 option 2) because it is a ~350L orchestration engine
// that reads flags, the capture provider, the store and a jotai atom.
import * as React from 'react';

import { toast } from '@heidi/design-system/toast';

import { documentSelectionRegenerateOutput } from '@/lib/analytics/scribe/document';
import { trackDocumentGenerated } from '@/lib/analytics/scribe/documents';
import { generateDocument } from '@/pages/api/scribeAPIV2';
import { paywallModalOpenFromQuotaExceeded } from '@heidi/analytics/events';
import {
  delayUpgradeExtendAction,
  QuotaExceedType,
  shouldShowDelayUpgradeModal,
} from '@heidi/commerce';
import { openQuotaExceededUpgradeModal } from '@heidi/commerce/data';
import { useTranslations } from '@heidi/runtime/i18n';
import { streamErrorMessage } from '@heidi/runtime/io';
import { DocumentGenType } from '@heidi/schema/ml-scribe';
import { transcriptDeletedMessages } from '@heidi/session';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import {
  fromRegenerateDocumentFailure,
  noteGenerationError,
  REGENERATE_DOCUMENT,
  useMutateRegenerateDocument as useMutateRegenerateDocumentCore,
  type GenerateDocumentPort,
  type RegenerateDocumentInput,
} from '@heidi/session/data';
import { invalidateAllTemplatesQuery } from '@heidi/templates/data';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import { GET_CLINICAL_TASKS_BY_SESSION_ID } from '../query/useQueryClinicalTasksBySessionId';
import { invalidateGamifyTrialExtension } from '../query/useQueryGamifyTrialExtension';
import { invalidateCurrentUser } from '../query/useQueryUser';

export { REGENERATE_DOCUMENT };
export type { RegenerateDocumentInput as GenerateDocumentProps };

/** Analytics vocabulary — app-owned, so the map stays here. */
const generationMethodMap = {
  [DocumentGenType.GPCCMP]: 'gpccmp' as const,
  [DocumentGenType.TEMPLATE]: 'template' as const,
  [DocumentGenType.INTERNAL_TEMPLATE]: 'internal_template' as const,
  [DocumentGenType.ASK_AI]: 'ask_ai' as const,
  [DocumentGenType.PRESET_AI_COMMAND]: 'ai_command' as const,
  [DocumentGenType.LIVE_DICTATION]: 'live_dictation' as const,
  [DocumentGenType.DICTATION]: 'dictation' as const,
  [DocumentGenType.PDF]: 'pdf' as const,
};

export function useMutateRegenerateDocument({
  sessionId,
  suppressGenericFailureToast = false,
}: {
  sessionId: string;
  /** For a caller that shows its own failure toast. */
  suppressGenericFailureToast?: boolean;
}) {
  const queryClient = useQueryClient();
  const setDelayUpgradeModalOpen = useSetAtom(shouldShowDelayUpgradeModal);
  const setDelayUpgradeExtendAction = useSetAtom(delayUpgradeExtendAction);
  const { t } = useTranslations();
  const intl = useIntl();

  const mutation = useMutateRegenerateDocumentCore({
    sessionId,
    generateDocument: generateDocument as GenerateDocumentPort,
    notTranscribedContent: t('userDocuments.sorryCouldntGenerateDocument'),
    invalidateCurrentUser: () => void invalidateCurrentUser(),
    invalidateGamifyTrialExtension: () => void invalidateGamifyTrialExtension(),
    invalidateAllTemplates: () => invalidateAllTemplatesQuery(),
    invalidateClinicalTasks: (id) =>
      queryClient.invalidateQueries({
        queryKey: [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId: id }],
      }),
    onGenerationAborted: ({ restored }) => {
      toast({
        title: restored
          ? t('userDocuments.generatingStoppedAndPreviousVersionRestored')
          : t('userDocuments.generatingStopped'),
      });
    },
    onRegenerated: ({
      documentId,
      templateId,
      previousGenerationType,
      isRegeneration,
    }) => {
      // tracking (old system - kept for backward compatibility)
      documentSelectionRegenerateOutput();

      // New unified tracking system for all document types
      if (previousGenerationType) {
        const methodKey =
          generationMethodMap[previousGenerationType as keyof typeof generationMethodMap];
        if (methodKey) {
          trackDocumentGenerated({
            session_id: sessionId,
            document_id: documentId,
            generation_method: methodKey,
            is_regeneration: isRegeneration,
            template_id: templateId,
          });
        }
      }
    },
    onRegenerateFailed: (failure, { documentId }) => {
      const error_code = fromRegenerateDocumentFailure(failure);
      if (error_code) {
        noteGenerationError({
          sessionId,
          error_code,
          documentId,
        });
      }
      switch (failure.kind) {
        case 'transcript-deleted':
          toast({
            tone: 'destructive',
            title: intl.formatMessage(transcriptDeletedMessages.blockedToastTitle),
            description: intl.formatMessage(
              transcriptDeletedMessages.blockedToastDescription
            ),
          });
          return;
        case 'session-not-transcribed':
          toast({
            tone: 'destructive',
            title: t('userDocuments.sessionNotTranscribed'),
            description: t('userDocuments.sessionNotTranscribedDescription'),
          });
          return;
        case 'stream-manually-aborted':
          return;
        case 'unlimited-quota-until-unset':
          setDelayUpgradeModalOpen(true);
          setDelayUpgradeExtendAction(QuotaExceedType.REGENERATE_DOCUMENT);
          return;
        case 'quota-exceeded':
          openQuotaExceededUpgradeModal();
          paywallModalOpenFromQuotaExceeded();
          return;
        default:
          if (suppressGenericFailureToast) return;
          toast({
            tone: 'destructive',
            title: t('userDocuments.failedToRegenerateDocument'),
            description: intl.formatMessage(streamErrorMessage(failure.status)),
          });
      }
    },
  });

  const mutateSyncChanges = React.useCallback(
    (input: RegenerateDocumentInput) => {
      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        (session) =>
          session
            ? {
                ...session,
                shouldShowDocumentSyncChanges: false,
              }
            : session
      );
      mutation.mutate(input);
    },
    [mutation, queryClient, sessionId]
  );

  return { ...mutation, mutateSyncChanges };
}
